import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import NavbarAr from "@/components/NavbarAr";
import Footer from "@/components/Footer";
import FooterAr from "@/components/FooterAr";
import Brand from "@/components/Brand";

// Privacy Policy — bilingual (EN / AR). Reuses the site's approved
// legal-page editorial pattern (see Rules.jsx). Content is professional
// and matched between languages (Arabic is not a machine-literal
// translation; both texts are legally aligned and the Arabic reads
// naturally in Saudi PDPL terminology).
//
// Controller: Masirat Al-Hayat for Entertainment (مسيرة الحياة للترفيه),
// operator of CrewZNatioN. Effective 8 August 2026 / ٨ أغسطس ٢٠٢٦.

const CONTROLLER_EN =
    "Masirat Al-Hayat for Entertainment (مسيرة الحياة للترفيه), operator of CrewZNatioN";
const CONTROLLER_AR = "مسيرة الحياة للترفيه، المُشغِّل لعلامة CrewZNatioN";
const PRIVACY_EMAIL = "admin@crewznation.com";
const SDAIA_URL = "https://sdaia.gov.sa/en/SDAIA/services/Pages/complaint.aspx";
const EFFECTIVE_EN = "8 August 2026";
const EFFECTIVE_AR = "٨ أغسطس ٢٠٢٦";

const SECTIONS = [
    {
        id: "introduction",
        en: {
            heading: "1. Introduction and scope",
            body: [
                <>
                    This Privacy Policy explains how <Brand /> handles personal data across its digital services — the website at crewznation.com, the CrewZNatioN mobile application, community and account services, Events, Garage/MY CREW, the Feed, the Hub/Marketplace, invitations, RSVP and QR scanning, and all associated support and administrative services.
                </>,
                <>
                    Certain information is <strong>private by default</strong> wherever a privacy control exists in the service. Data becomes public or community-visible only when a member intentionally publishes it, invites another member, or selects a public setting. Where no visibility control is offered for a given feature, the information is treated as internal to <Brand /> and used only to operate that feature.
                </>,
                <>
                    <Brand /> operates from Riyadh, Kingdom of Saudi Arabia, and this policy applies together with the applicable <Brand /> Terms and any feature-specific notice a member sees at the point of collection.
                </>,
            ],
        },
        ar: {
            heading: "١. المقدمة والنطاق",
            body: [
                <>
                    توضح سياسة الخصوصية هذه كيف تتعامل <Brand /> مع البيانات الشخصية عبر خدماتها الرقمية: موقع crewznation.com، وتطبيق CrewZNatioN للأجهزة الجوّالة، وخدمات المجتمع والحسابات، والفعاليات، وقسم &quot;الجراج/طاقمي&quot; (Garage / MY CREW)، والتغذية (Feed)، ومنصّة &quot;الهَب/السوق&quot; (Hub / Marketplace)، والدعوات، وتأكيدات الحضور، ومسح رموز QR، وجميع خدمات الدعم والإدارة المرتبطة بها.
                </>,
                <>
                    تُعامَل بعض البيانات باعتبارها <strong>خاصة تلقائيًا</strong> متى ما توفّرت في الخدمة أداة تحكّم في الخصوصية. ولا تصبح البيانات عامةً أو ظاهرة للمجتمع إلا عندما يختار العضو نشرها بنفسه، أو دعوة عضوٍ آخر، أو ضبطها ضمن إعدادٍ عام. وفي حال عدم توفّر أداة تحكّمٍ لخاصية معيّنة، فإن البيانات تُعامَل بوصفها بياناتٍ داخليةً لدى <Brand /> وتُستخدَم فقط لتشغيل تلك الخاصية.
                </>,
                <>
                    تعمل <Brand /> انطلاقًا من مدينة الرياض، المملكة العربية السعودية. وتُطبَّق هذه السياسة مع شروط استخدام <Brand /> المعتمدة ومع أي إشعارٍ خاصٍّ بميزةٍ محدّدة يُعرض على العضو عند نقطة جمع البيانات.
                </>,
            ],
        },
    },
    {
        id: "controller",
        en: {
            heading: "2. Who controls the data and how to contact us",
            body: [
                <>
                    The controller of personal data is {CONTROLLER_EN}. All privacy requests, questions, or complaints may be sent to <a className="text-[var(--crewz-gold)] hover:underline" href={`mailto:${PRIVACY_EMAIL}?subject=Privacy%20Request`}>{PRIVACY_EMAIL}</a> using the subject line <em>Privacy Request</em>.
                </>,
            ],
        },
        ar: {
            heading: "٢. الجهة المتحكِّمة بالبيانات وكيفية التواصل",
            body: [
                <>
                    الجهة المتحكِّمة بالبيانات الشخصية هي {CONTROLLER_AR}. يمكن إرسال جميع طلبات الخصوصية، والاستفسارات، والشكاوى إلى <a className="text-[var(--crewz-gold)] hover:underline" dir="ltr" href={`mailto:${PRIVACY_EMAIL}?subject=%D8%B7%D9%84%D8%A8%20%D8%AE%D8%B5%D9%88%D8%B5%D9%8A%D8%A9`}>{PRIVACY_EMAIL}</a> باستخدام موضوع الرسالة <em>طلب خصوصية</em>.
                </>,
            ],
        },
    },
    {
        id: "eligibility",
        en: {
            heading: "3. Eligibility",
            body: [
                    <>
                    <Brand /> services and accounts are intended for persons aged <strong>18 and above</strong>. Registration is restricted accordingly, and features may verify age eligibility during account creation or before use.
                </>,
            ],
        },
        ar: {
            heading: "٣. الأهلية",
            body: [
                <>
                    خدمات وحسابات <Brand /> مخصصة للأشخاص الذين تبلغ أعمارهم <strong>١٨ عامًا فأكثر</strong>. ويُقيَّد التسجيل وفقًا لهذا الشرط، وقد تتحقّق بعض الميزات من الأهلية العمرية عند إنشاء الحساب أو قبل استخدامها.
                </>,
            ],
        },
    },
    {
        id: "data-collected",
        en: {
            heading: "4. Personal data we collect",
            body: [
                "The data collected depends on the feature used and is limited to what is provided or required in the relevant form.",
            ],
            list: [
                <><strong>Account, identity and contact:</strong> name, preferred/display name, username, email, mobile number, date of birth and age-eligibility indicator, gender where collected, Instagram handle, interests and preferences, profile photo, bio, account type, and authentication and consent records.</>,
                <><strong>Vehicle-owner application and Garage:</strong> vehicle make, model, year, category, specifications and modifications, ownership and eligibility information, vehicle photos and videos, captions, Garage privacy setting, access requests, and upload-rights acknowledgments.</>,
                <><strong>Feed and community content:</strong> text, photos, videos, uploaded GIF files, GIF-search queries and selections, comments, reactions/likes, MY CREW relationships, reports and moderation history.</>,
                <><strong>Events:</strong> applications, invitations, RSVP status, guest and participant information, interests, event communications, unique QR/pass identifiers, scan and check-in timestamps, and event attendance records.</>,
                <><strong>Hub/Marketplace:</strong> listing title, description, category, price, photos, seller identity and contact route, listing edits and deletions, and reports. <Brand /> V1 is not the merchant of record and does not collect payment-card data for member Marketplace listings unless a future feature is separately disclosed.</>,
                <><strong>Support and administration:</strong> enquiries, contact forms, emails, complaints, appeals, safety reports, administrative decisions and audit records.</>,
                <><strong>Technical and device data:</strong> IP address, timestamps, security and authentication logs, browser, app, device and operating-system information, crash and error diagnostics where enabled, cookies and local storage, and feature permissions.</>,
                <><strong>Device permissions:</strong> camera, photo library/files and location are requested only when the member invokes a feature that requires them. There is no continuous background-location tracking unless separately disclosed and consented.</>,
                <><strong>External actions:</strong> when the member chooses to use Maps/Places, Calendar, email, Instagram or another external service, only the information needed for that action is transmitted at the member&apos;s direction.</>,
            ],
        },
        ar: {
            heading: "٤. البيانات الشخصية التي نجمعها",
            body: [
                "تعتمد البيانات التي تُجمَع على الميزة المستخدَمة، وتقتصر على ما يُقدَّم أو يُطلَب في النموذج ذي الصلة.",
            ],
            list: [
                <><strong>الحساب والهُوية والتواصل:</strong> الاسم، والاسم المُفضَّل/الظاهر، واسم المستخدم، والبريد الإلكتروني، ورقم الجوّال، وتاريخ الميلاد ومؤشِّر الأهلية العمرية، والجنس حيثما يُجمَع، واسم حساب إنستجرام، والاهتمامات والتفضيلات، وصورة الملف الشخصي، والنبذة، ونوع الحساب، وسجلّات المصادقة والموافقات.</>,
                <><strong>طلب صاحب المركبة والجراج:</strong> ماركة المركبة، والطراز، وسنة الصنع، والفئة، والمواصفات والتعديلات، ومعلومات الملكية والأهلية، وصور المركبة ومقاطع الفيديو، والتعليقات المصاحبة، وإعداد خصوصية الجراج، وطلبات الوصول، وإقرارات صلاحية الرفع.</>,
                <><strong>محتوى التغذية والمجتمع:</strong> النصوص، والصور، ومقاطع الفيديو، وملفات صور GIF المرفوعة، وعمليات البحث في مكتبة GIF واختياراتها، والتعليقات، والتفاعلات/الإعجابات، وعلاقات &quot;طاقمي&quot; (MY CREW)، والبلاغات وسجلّ الإجراءات التنظيمية.</>,
                <><strong>الفعاليات:</strong> الطلبات، والدعوات، وحالة تأكيد الحضور، ومعلومات الضيوف/المشاركين، والاهتمامات، ومراسلات الفعاليات، ومعرِّفات QR/التصاريح الفريدة، وأختام زمنية للمسح وتسجيل الحضور، وسجلّات حضور الفعاليات.</>,
                <><strong>الهَب/السوق:</strong> عنوان الإعلان، ووصفه، وفئته، وسعره، وصوره، وهوية البائع ووسيلة التواصل، وتعديلات الإعلانات وحذفها، والبلاغات المتعلّقة بها. لا تُعدّ <Brand /> في الإصدار الأول (V1) البائعَ المُسجَّل للمنتجات، ولا تجمع بيانات بطاقات الدفع الخاصة بإعلانات الأعضاء في السوق، ما لم يُفصَح عن ميزةٍ مستقبليةٍ بشكلٍ منفصل.</>,
                <><strong>الدعم والإدارة:</strong> الاستفسارات، ونماذج التواصل، والرسائل الإلكترونية، والشكاوى، والاعتراضات، وبلاغات السلامة، والقرارات الإدارية، وسجلّات التدقيق.</>,
                <><strong>البيانات التقنية وبيانات الجهاز:</strong> عنوان بروتوكول الإنترنت (IP)، والأختام الزمنية، وسجلّات الأمن والمصادقة، ومعلومات المتصفّح والتطبيق والجهاز ونظام التشغيل، وتشخيصات الأعطال والأخطاء عند تفعيلها، وملفات تعريف الارتباط والتخزين المحلي، وأذونات الميزات.</>,
                <><strong>أذونات الجهاز:</strong> يُطلَب الوصول إلى الكاميرا، ومكتبة الصور/الملفات، والموقع الجغرافي فقط عندما يستدعي العضو ميزةً تتطلّب ذلك. ولا يوجد تتبّعٌ مستمر للموقع في الخلفية ما لم يُفصَح عن ذلك ويُوافِق عليه العضو بشكلٍ منفصل.</>,
                <><strong>الإجراءات الخارجية:</strong> عندما يختار العضو استخدام خرائط/أماكن جوجل، أو التقويم، أو البريد، أو إنستجرام، أو أي خدمةٍ خارجيةٍ أخرى، لا تُرسَل سوى المعلومات اللازمة لتلك العملية وبناءً على توجيه العضو.</>,
            ],
        },
    },
    {
        id: "how-collected",
        en: {
            heading: "5. How we collect data",
            body: [
                <>Data is collected directly from the member when using the service; automatically through use and security logs; from other members or administrator reports; and from public sources or connected services only where lawful and reasonably expected.</>,
            ],
        },
        ar: {
            heading: "٥. كيف نجمع البيانات",
            body: [
                <>تُجمَع البيانات مباشرةً من العضو أثناء استخدام الخدمة، وتلقائيًّا من خلال سجلّات الاستخدام والأمن، ومن الأعضاء الآخرين أو من بلاغات الإدارة، ومن المصادر العامة أو الخدمات المتّصلة فقط حيثما يكون ذلك نظاميًّا ومتوقّعًا بصورةٍ معقولة.</>,
            ],
        },
    },
    {
        id: "purposes",
        en: {
            heading: "6. Purposes",
            body: ["Data is used to operate the service and to enable the features the member has chosen, including:"],
            list: [
                "creating and authenticating accounts;",
                "verifying age, account type and vehicle-owner eligibility;",
                <>providing profiles, Garage, Feed, comments, uploaded media and MY CREW;</>,
                "operating Events, invitations, RSVP, QR passes and scanner;",
                "operating Hub/Marketplace;",
                "respecting privacy choices and access controls;",
                "safety, moderation and fraud prevention;",
                "support and administrative response;",
                "essential transactional communications (account, security, event, listing, reply);",
                "optional promotional communications where the member has separately opted in;",
                "legal and regulatory compliance;",
                "service measurement and improvement;",
                "aggregated and anonymized research and community insights.",
            ],
        },
        ar: {
            heading: "٦. أغراض المعالجة",
            body: ["تُستخدَم البيانات لتشغيل الخدمة ولتمكين الميزات التي اختارها العضو، بما في ذلك:"],
            list: [
                "إنشاء الحسابات والتحقّق من هوية أصحابها؛",
                "التحقّق من الأهلية العمرية ونوع الحساب وأهلية صاحب المركبة؛",
                <>تقديم الملفات الشخصية، والجراج، والتغذية، والتعليقات، والوسائط المرفوعة، و&quot;طاقمي&quot;؛</>,
                "تشغيل الفعاليات والدعوات وتأكيدات الحضور وتصاريح QR والماسح الضوئي؛",
                "تشغيل الهَب/السوق؛",
                "احترام إعدادات الخصوصية وضوابط الوصول؛",
                "السلامة والرقابة على المحتوى ومكافحة الاحتيال؛",
                "الدعم والاستجابة الإدارية؛",
                "المراسلات التشغيلية الأساسية (الحساب، الأمن، الفعالية، الإعلان، الردود)؛",
                "المراسلات الترويجية الاختيارية عند اشتراك العضو فيها بموافقةٍ منفصلة؛",
                "الالتزام بالمتطلّبات النظامية والتنظيمية؛",
                "قياس الخدمة وتحسينها؛",
                "الأبحاث ورؤى المجتمع باستخدام بياناتٍ مجمَّعة أو مجهولة الهُوية.",
            ],
        },
    },
    {
        id: "legal-bases",
        en: {
            heading: "7. Legal bases under the Saudi PDPL",
            body: [
                <>Depending on the activity, <Brand /> relies on one or more of the following legal bases under the Personal Data Protection Law of the Kingdom of Saudi Arabia and its Implementing Regulations:</>,
            ],
            list: [
                "consent, where the law requires consent;",
                "steps and arrangements necessary to provide the services the member has requested and to enforce accepted terms;",
                "compliance with legal obligations and judicial or regulatory requirements;",
                "legitimate interests that do not override the member's rights and that do not rely on sensitive data;",
                "public-source data where its use is permitted by law;",
                "vital or public-safety grounds where applicable.",
            ],
            footer: [
                <>No single universal basis is claimed for all processing.</>,
            ],
        },
        ar: {
            heading: "٧. الأسس النظامية بموجب نظام حماية البيانات الشخصية السعودي",
            body: [
                <>وفقًا للنشاط، تعتمد <Brand /> على واحدٍ أو أكثر من الأسس النظامية التالية بموجب نظام حماية البيانات الشخصية في المملكة العربية السعودية ولوائحه التنفيذية:</>,
            ],
            list: [
                "الموافقة، حيثما يشترط النظام الحصول عليها؛",
                "الخطوات والترتيبات اللازمة لتقديم الخدمات التي طلبها العضو ولتنفيذ الشروط المقبولة؛",
                "الامتثال للالتزامات النظامية والمتطلبات القضائية والتنظيمية؛",
                "المصالح المشروعة التي لا تتجاوز حقوق العضو ولا تعتمد على بياناتٍ حسّاسة؛",
                "البيانات المتاحة من المصادر العامة حيثما يجيز النظام استخدامها؛",
                "دواعي الحياة أو السلامة العامة حيثما ينطبق ذلك.",
            ],
            footer: [
                <>ولا يُدَّعى وجود أساسٍ عامٍّ واحدٍ يُطبَّق على جميع أنشطة المعالجة.</>,
            ],
        },
    },
    {
        id: "visibility",
        en: {
            heading: "8. Public and private visibility",
            body: [
                <>Some information is never made public. Registration data, email, mobile number, date of birth, consent records, QR/pass data, scan history, private reports and administrative records are internal to <Brand /> and are not visible to other members.</>,
                <>Other information may become visible to the community according to the feature and the member&apos;s deliberate setting or action: display name and username, avatar and bio, Instagram handle, posts and comments and media, a public Garage, public participation indicators for a given event, and Marketplace listings.</>,
                <>Private Garage content and access-controlled records are limited to viewers the member has approved and to authorized staff with a need to act. Public recipients may screenshot or copy content outside <Brand />&apos;s control; members should consider this before publishing.</>,
                <>Public visibility does not give <Brand /> unrelated promotional rights. Any promotional reuse of a member&apos;s identifiable content or identity requires a separate, approved consent.</>,
            ],
        },
        ar: {
            heading: "٨. الظهور العام والخاص",
            body: [
                <>لا تصبح بعض البيانات علنيةً على الإطلاق. فبيانات التسجيل، والبريد الإلكتروني، ورقم الجوّال، وتاريخ الميلاد، وسجلّات الموافقات، وبيانات رموز QR/التصاريح، وسجلّ عمليات المسح، والبلاغات الخاصة، والسجلّات الإدارية تُعدّ داخليةً لدى <Brand /> ولا تظهر لبقية الأعضاء.</>,
                <>وقد تظهر معلوماتٌ أخرى للمجتمع وفقًا للميزة ولإعداد العضو أو إجرائه المتعمَّد: الاسم الظاهر واسم المستخدم، والصورة الرمزية والنبذة، واسم حساب إنستجرام، والمنشورات والتعليقات والوسائط، والجراج العام، ومؤشِّرات المشاركة العلنية في فعاليةٍ معيّنة، وإعلانات السوق.</>,
                <>يقتصر محتوى الجراج الخاص والسجلّات الخاضعة لضوابط الوصول على المشاهدين الذين اعتمدهم العضو وعلى الموظفين المخوَّلين ممّن يحتاجون إلى ذلك لأداء واجباتهم. ويجدر بالعضو مراعاة أن المستقبِلين العامّين قد يلتقطون صورًا للشاشة أو ينسخون المحتوى خارج نطاق سيطرة <Brand />.</>,
                <>ولا يمنح الظهور العام <Brand /> أي حقوقٍ ترويجيةٍ غير مرتبطة بالميزة نفسها. وأي إعادة استخدامٍ ترويجيٍّ لمحتوى العضو أو هويّته المُعرِّفة يستلزم موافقةً منفصلةً ومعتَمدة.</>,
            ],
        },
    },
    {
        id: "sharing",
        en: {
            heading: "9. Sharing and disclosure",
            body: [<>Personal data is shared only as follows:</>],
            list: [
                "with other members and visitors, according to the member's visibility choices;",
                "with vetted service providers strictly required to run the service (see Section 10);",
                "with event operations, security and check-in staff only to the extent needed to run a specific event;",
                "with authorities, regulators or courts where legally required, or to protect the safety of a person or the community;",
                "in no case are we selling personal data;",
                <>with sponsors or partners only in aggregated or anonymized form, unless the member has separately and expressly consented to an identifiable disclosure;</>,
                <>in the event of a business restructuring or transfer, only subject to law, prior notice and equivalent privacy protection.</>,
            ],
        },
        ar: {
            heading: "٩. المشاركة والإفصاح",
            body: [<>لا تُشارَك البيانات الشخصية إلا على النحو التالي:</>],
            list: [
                "مع بقية الأعضاء والزوّار، وفقًا لخيارات الظهور التي حدّدها العضو؛",
                "مع مزوّدي خدماتٍ خاضعين للتقييم ولازمين حصرًا لتشغيل الخدمة (يُنظر إلى الفقرة ١٠)؛",
                "مع القائمين على تشغيل الفعاليات، والأمن، وموظفي الاستقبال بالقدر اللازم لتنفيذ فعاليةٍ محدّدة؛",
                "مع الجهات النظامية أو التنظيمية أو القضائية حين يشترط النظام ذلك، أو حمايةً لسلامة شخصٍ أو المجتمع؛",
                "لا تقوم CrewZNatioN ببيع البيانات الشخصية في أي حالٍ من الأحوال؛",
                <>مع الرعاة أو الشركاء فقط بصيغةٍ مجمَّعةٍ أو مجهولة الهُوية، ما لم يمنح العضو موافقةً منفصلةً وصريحةً على إفصاحٍ يمكن تعريفه به؛</>,
                <>وفي حال إعادة هيكلةٍ أو نقلٍ للنشاط، لا يجري ذلك إلا وفق النظام، وبعد إشعارٍ مسبق، وبحمايةٍ مماثلةٍ للخصوصية.</>,
            ],
        },
    },
    {
        id: "providers",
        en: {
            heading: "10. Service-provider examples and separate data paths",
            body: [
                <><Brand /> uses a small number of vetted providers, and only where the feature is enabled:</>,
            ],
            list: [
                <><strong>Resend</strong> — transactional email delivery when enabled;</>,
                <><strong>Google Maps / Places / Calendar</strong> — invoked at the member&apos;s direction when using those features;</>,
                <><strong>GIPHY</strong> — searchable GIF-library queries and selections when enabled;</>,
                <><strong>Member-uploaded GIF files</strong> — a separate upload path that does not require GIPHY.</>,
            ],
            footer: [
                <>These integrations are described using &quot;where enabled&quot; because production credentials for some providers may not yet be active. Each provider&apos;s own privacy terms also apply when the member chooses to use that provider.</>,
            ],
        },
        ar: {
            heading: "١٠. أمثلة مزوّدي الخدمات ومسارات البيانات المستقلة",
            body: [
                <>تعتمد <Brand /> على عددٍ محدودٍ من مزوّدي الخدمات الذين خضعوا للتقييم، وفقط عند تفعيل الميزة ذات الصلة:</>,
            ],
            list: [
                <><strong>Resend</strong> — إرسال البريد الإلكتروني التشغيلي عند تفعيله؛</>,
                <><strong>Google Maps / Places / Calendar</strong> — تُستدعى بناءً على توجيه العضو عند استخدام هذه الميزات؛</>,
                <><strong>GIPHY</strong> — البحث في مكتبة صور GIF واختياراتها عند تفعيلها؛</>,
                <><strong>ملفات GIF المرفوعة من الأعضاء</strong> — مسار رفعٍ منفصل لا يتطلّب استخدام GIPHY.</>,
            ],
            footer: [
                <>وقد اعتُمِدت صيغة &quot;عند التفعيل&quot; لأن بيانات الإنتاج الخاصة ببعض المزوّدين قد لا تكون مفعَّلةً بعد. كما تنطبق شروط الخصوصية الخاصة بكل مزوّدٍ متى ما اختار العضو استخدام خدمته.</>,
            ],
        },
    },
    {
        id: "transfers",
        en: {
            heading: "11. International transfers",
            body: [
                <>Processing may take place in the Kingdom of Saudi Arabia and in countries where contracted cloud or service providers operate. Not all data is stored exclusively inside the Kingdom.</>,
                <>Any transfer of personal data outside the Kingdom is carried out subject to the Saudi PDPL and the Regulation on Personal Data Transfer Outside the Kingdom, applying data minimisation, a documented transfer assessment, and appropriate contractual and technical safeguards.</>,
            ],
        },
        ar: {
            heading: "١١. عمليات النقل الدولية",
            body: [
                <>قد تجري المعالجة داخل المملكة العربية السعودية وفي الدول التي يعمل بها مزوّدو الخدمات السحابية أو الخدمات المتعاقَد معهم. ولا تُخزَّن جميع البيانات حصريًّا داخل المملكة.</>,
                <>وتخضع أي عمليةٍ لنقل البيانات الشخصية خارج المملكة لأحكام نظام حماية البيانات الشخصية ولائحة نقل البيانات الشخصية خارج المملكة، مع تطبيق مبدأ تقليل البيانات، وتقييمٍ موثَّقٍ لعملية النقل، والضمانات التعاقدية والتقنية المناسبة.</>,
            ],
        },
    },
    {
        id: "retention",
        en: {
            heading: "12. Retention and deletion",
            body: [
                <>Personal data is retained only for as long as needed, according to the following conservative schedule. A longer period applies only when the law, a dispute, a safety incident or a judicial requirement requires it.</>,
            ],
            list: [
                "active account, profile, Garage and content — while the account or feature remains active and needed;",
                <>following an approved account-closure request — remove or anonymize active-service personal data within <strong>90 days</strong>, subject to the exceptions below;</>,
                <>deleted posts, media, comments and Marketplace listings — removed from active display promptly; residual encrypted backups may persist for up to <strong>90 days</strong> before overwrite;</>,
                <>Event RSVP, QR/pass and check-in records — up to <strong>24 months</strong> after the relevant event;</>,
                <>support, safety, moderation, security and fraud-prevention records — up to <strong>3 years</strong> after case closure;</>,
                <>consent, withdrawal and compliance records — for the period necessary to demonstrate compliance, including any applicable statutory period; internal records of processing activities are retained for <strong>five years</strong> after the relevant processing activity ends;</>,
                <>anonymized research and statistical data — may be retained without a fixed period only after irreversible anonymization;</>,
                <>on expiry, data is destroyed or anonymized securely.</>,
            ],
        },
        ar: {
            heading: "١٢. الاحتفاظ بالبيانات وحذفها",
            body: [
                <>يُحتفَظ بالبيانات الشخصية فقط للمدّة اللازمة، وفق الجدول التحفّظي التالي. ولا يُمَدَّد الاحتفاظ إلا عندما يقتضي النظام، أو نزاعٌ قائم، أو حادث سلامة، أو متطلَّبٌ قضائي ذلك.</>,
            ],
            list: [
                "الحساب النشِط والملف الشخصي والجراج والمحتوى — طوال بقاء الحساب أو الميزة نشِطةً وقائمةً على الحاجة؛",
                <>عقب طلبٍ معتَمَدٍ لإغلاق الحساب — تُحذَف بيانات الخدمة النشِطة أو تُجهَّل خلال <strong>٩٠ يومًا</strong>، وفق الاستثناءات أدناه؛</>,
                <>المنشورات والوسائط والتعليقات وإعلانات السوق المحذوفة — تُزال من العرض فورًا، وقد تبقى نسخٌ احتياطيةٌ مشفَّرة لمدةٍ تصل إلى <strong>٩٠ يومًا</strong> قبل استبدالها؛</>,
                <>سجلّات تأكيد الحضور، ورموز QR/التصاريح، وتسجيل الحضور للفعاليات — لمدةٍ تصل إلى <strong>٢٤ شهرًا</strong> بعد الفعالية المعنية؛</>,
                <>سجلّات الدعم، والسلامة، والرقابة على المحتوى، والأمن، ومكافحة الاحتيال — لمدةٍ تصل إلى <strong>٣ سنوات</strong> بعد إغلاق الحالة؛</>,
                <>سجلّات الموافقات، وسحبها، وسجلّات الامتثال — للمدّة اللازمة لإثبات الامتثال، بما في ذلك أي مدةٍ نظاميةٍ سارية؛ وتُحفَظ السجلّات الداخلية لأنشطة المعالجة لمدة <strong>خمس سنوات</strong> بعد انتهاء نشاط المعالجة المعني؛</>,
                <>البيانات البحثية والإحصائية بعد تجهيلها بشكلٍ لا يمكن عكسه — يمكن الاحتفاظ بها دون مدةٍ ثابتة؛</>,
                <>عند انتهاء المدة، تُتلَف البيانات أو تُجهَّل بأسلوبٍ آمن.</>,
            ],
        },
    },
    {
        id: "security",
        en: {
            heading: "13. Security",
            body: [
                <>Protections include role-based / need-to-know access, authentication, encryption of data in transit where supported, logging and monitoring, backups, processor controls and an incident-response process. No system can guarantee absolute security. Where Saudi law requires, <Brand /> notifies the competent authority and affected members of a personal-data incident.</>,
            ],
        },
        ar: {
            heading: "١٣. الأمن",
            body: [
                <>تشمل الحمايات المطبَّقة الوصول القائم على الأدوار وعلى مبدأ &quot;الحاجة إلى المعرفة&quot;، والمصادقة، وتشفير البيانات أثناء نقلها حيثما كان ذلك ممكنًا، والتسجيل والمراقبة، والنسخ الاحتياطي، وضوابط المُعالِج، ومنظومة الاستجابة للحوادث. ولا يمكن لأي نظامٍ ضمان أمانٍ مطلق. وحين يشترط النظام السعودي ذلك، تُبلِغ <Brand /> الجهة المختصّة والأعضاء المتأثرين بأي حادثٍ يتعلّق بالبيانات الشخصية.</>,
            ],
        },
    },
    {
        id: "rights",
        en: {
            heading: "14. Member rights under the Saudi PDPL",
            body: [<>Members have the following rights, subject to the conditions of the Saudi PDPL:</>],
            list: [
                "the right to be informed of how personal data is processed;",
                "the right to access personal data held about them;",
                "the right to obtain a readable copy of that data;",
                "the right to correct, complete or update inaccurate data;",
                "the right to request destruction of the data where applicable;",
                "the right to withdraw consent where processing relies on consent;",
                "the right to object and the right to complain.",
            ],
            footer: [
                <>Requests are handled without charge unless the law permits otherwise. Identity may be verified with the minimum information necessary. <Brand /> will act on a valid request within <strong>30 days</strong>. A lawful extension of up to a further <strong>30 days</strong> may apply, with advance notice of the reason.</>,
            ],
        },
        ar: {
            heading: "١٤. حقوق العضو بموجب نظام حماية البيانات الشخصية",
            body: [<>يتمتّع العضو بالحقوق التالية، وفق شروط نظام حماية البيانات الشخصية السعودي:</>],
            list: [
                "الحق في معرفة كيفية معالجة بياناته الشخصية؛",
                "الحق في الوصول إلى بياناته الشخصية؛",
                "الحق في الحصول على نسخةٍ مقروءةٍ من بياناته؛",
                "الحق في تصحيح البيانات غير الدقيقة أو استكمالها أو تحديثها؛",
                "الحق في طلب إتلاف البيانات حيثما ينطبق ذلك؛",
                "الحق في سحب الموافقة حين تكون المعالجة قائمةً عليها؛",
                "الحق في الاعتراض، والحق في تقديم الشكوى.",
            ],
            footer: [
                <>تُعالَج الطلبات دون رسوم ما لم يُجِز النظام غير ذلك. وقد يُتحقَّق من هوية مقدّم الطلب بالحدّ الأدنى من المعلومات اللازمة. وتلتزم <Brand /> بمعالجة الطلب النظامي خلال <strong>٣٠ يومًا</strong>. وقد تُمَدَّد المدّة نظاميًّا لمدةٍ إضافية أقصاها <strong>٣٠ يومًا</strong>، مع إشعارٍ مسبقٍ يوضّح السبب.</>,
            ],
        },
    },
    {
        id: "exercise",
        en: {
            heading: "15. How to exercise rights or delete an account",
            body: [
                <>
                    To exercise a right or request account deletion, email <a className="text-[var(--crewz-gold)] hover:underline" href={`mailto:${PRIVACY_EMAIL}?subject=Privacy%20Request`}>{PRIVACY_EMAIL}</a> with the subject <em>Privacy Request</em>, identifying the account and the request. In-app self-service tools may also be available where offered. <Brand /> does not request a government ID by default and asks only for the minimum verification necessary.
                </>,
                <>
                    Account deletion is scheduled, not immediate. When a member confirms a deletion, the account is disabled and hidden from other members and a <strong>30-day cancellation window</strong> begins. During that window the member can sign back in and cancel the deletion. On day 30 the cancellation window closes and destructive processing begins; the account and its associated personal data / user-generated content are then permanently deleted or irreversibly anonymized, subject only to the narrow legal, security, safety and accounting retention exceptions described in section 12.
                </>,
                <>
                    Detailed step-by-step instructions for closing a <Brand /> account, including the in-app path, the written request path, identity verification, the 30-day cancellation window, and what data is deleted, anonymized or retained, are published on the <Link to="/account-deletion" className="text-[var(--crewz-gold)] hover:underline">Account Deletion</Link> page.
                </>,
            ],
        },
        ar: {
            heading: "١٥. آلية ممارسة الحقوق أو حذف الحساب",
            body: [
                <>
                    لممارسة أي حق، أو طلب حذف الحساب، تُرسَل رسالةٌ إلى <a className="text-[var(--crewz-gold)] hover:underline" dir="ltr" href={`mailto:${PRIVACY_EMAIL}?subject=%D8%B7%D9%84%D8%A8%20%D8%AE%D8%B5%D9%88%D8%B5%D9%8A%D8%A9`}>{PRIVACY_EMAIL}</a> بموضوع <em>طلب خصوصية</em>، مع تحديد الحساب وطبيعة الطلب. وقد تتوفّر أدواتٌ للخدمة الذاتية داخل التطبيق حيثما أُتيحت. ولا تطلب <Brand /> الهوية الحكومية تلقائيًّا، بل تكتفي بالحدّ الأدنى الضروري للتحقّق.
                </>,
                <>
                    حذف الحساب مُجدوَلٌ لا فوري. فعند تأكيد العضو للحذف، يُعطَّل الحساب ويُخفى عن بقية الأعضاء، وتبدأ <strong>مهلة إلغاءٍ مدّتها ٣٠ يومًا</strong>. ويمكن للعضو خلال هذه المهلة تسجيل الدخول وإلغاء الحذف. وفي اليوم الثلاثين تُغلَق مهلة الإلغاء وتبدأ عملية الحذف الفعلية؛ فيُحذَف الحساب وبياناته الشخصية والمحتوى المرتبط به بشكلٍ نهائي أو يُجهَّل بشكلٍ لا يمكن عكسه، مع الاستثناءات المحدَّدة للاحتفاظ لأغراضٍ نظاميةٍ أو أمنيةٍ أو متعلّقةٍ بالسلامة أو المحاسبة الواردة في الفقرة ١٢.
                </>,
                <>
                    وتتوفّر خطوات تفصيلية لإغلاق حساب <Brand />، تشمل مسار الحذف داخل التطبيق، ومسار الطلب الكتابي، والتحقّق من الهوية، ومهلة الإلغاء البالغة ٣٠ يومًا، وما يُحذَف أو يُجهَّل أو يُحتفَظ به من بيانات، على صفحة <Link to="/ar/account-deletion" className="text-[var(--crewz-gold)] hover:underline">حذف الحساب</Link>.
                </>,
            ],
        },
    },
    {
        id: "complaints",
        en: {
            heading: "16. Complaints",
            body: [
                <>Members are encouraged to contact <Brand /> first at <a className="text-[var(--crewz-gold)] hover:underline" href={`mailto:${PRIVACY_EMAIL}?subject=Privacy%20Request`}>{PRIVACY_EMAIL}</a>. If a matter is unresolved, or a right cannot be exercised, a complaint may be filed with the Saudi Data & AI Authority (SDAIA) through the National Data Governance Platform:{" "}
                    <a href={SDAIA_URL} target="_blank" rel="noreferrer" className="text-[var(--crewz-gold)] hover:underline break-all">
                        {SDAIA_URL}
                    </a>.
                </>,
            ],
        },
        ar: {
            heading: "١٦. الشكاوى",
            body: [
                <>يُشجَّع الأعضاء على التواصل مع <Brand /> أوّلًا عبر <a className="text-[var(--crewz-gold)] hover:underline" dir="ltr" href={`mailto:${PRIVACY_EMAIL}?subject=%D8%B7%D9%84%D8%A8%20%D8%AE%D8%B5%D9%88%D8%B5%D9%8A%D8%A9`}>{PRIVACY_EMAIL}</a>. وفي حال تعذّر معالجة الطلب أو ممارسة الحق، يمكن تقديم شكوى إلى الهيئة السعودية للبيانات والذكاء الاصطناعي (سدايا) عبر منصّة حوكمة البيانات الوطنية:{" "}
                    <a href={SDAIA_URL} target="_blank" rel="noreferrer" dir="ltr" className="text-[var(--crewz-gold)] hover:underline break-all">
                        {SDAIA_URL}
                    </a>.
                </>,
            ],
        },
    },
    {
        id: "permissions",
        en: {
            heading: "17. Permissions and external services",
            body: [
                <>Device permissions (camera, photo library, location) can be changed at any time in device settings. Denying a permission may disable only the specific feature that depends on it. When a member chooses to use an external service (Maps/Places/Calendar/Instagram/email), that provider&apos;s own privacy terms also apply to that action.</>,
            ],
        },
        ar: {
            heading: "١٧. الأذونات والخدمات الخارجية",
            body: [
                <>يمكن تغيير أذونات الجهاز (الكاميرا، ومكتبة الصور، والموقع الجغرافي) في أي وقتٍ من إعدادات الجهاز. وقد يؤدّي رفض الإذن إلى تعطيل الميزة التي تعتمد عليه فقط. وعندما يختار العضو استخدام خدمةٍ خارجية (Maps/Places/Calendar/Instagram/البريد)، تنطبق شروط الخصوصية الخاصة بذلك المزوّد على تلك العملية.</>,
            ],
        },
    },
    {
        id: "cookies",
        en: {
            heading: "18. Cookies, website storage, and third-party services",
            body: [
                <>The website uses only strictly necessary cookies and browser local storage: an authentication token so a signed-in member remains signed in across page loads, and standard preference storage (for example, the visitor&apos;s language selection). Web fonts are served from Google Fonts&apos; content-delivery network.</>,
                <><Brand /> does <strong>not</strong> operate any analytics, session-replay, advertising, retargeting, cross-site tracking, or behavioural-monitoring technology on this website. No Meta / Facebook pixel, no Google Analytics, no PostHog, no chat widgets, no form trackers, and no third-party fingerprinting scripts are loaded.</>,
                <>Should <Brand /> introduce any non-essential analytics in the future, this section will be updated and an appropriate notice-and-consent mechanism will be added before the technology is activated.</>,
            ],
        },
        ar: {
            heading: "١٨. ملفات تعريف الارتباط والتخزين وخدمات الأطراف الثالثة",
            body: [
                <>يستخدم الموقع فقط ملفات تعريف الارتباط والتخزين المحلي الضرورية بحصر: رمز مصادقةٍ يحافظ على تسجيل دخول العضو عبر تحميلات الصفحات، وتخزين تفضيلاتٍ أساسي (كخيار لغة الزائر). أما خطوط الويب فتُقدَّم من شبكة توزيع المحتوى الخاصة بـ Google Fonts.</>,
                <>لا تشغِّل <Brand /> على هذا الموقع <strong>أي</strong> تقنيات تحليلات، أو تسجيل جلسات، أو إعلانات، أو استهدافٍ إعلاني، أو تتبّعٍ عبر المواقع، أو مراقبةٍ سلوكية. ولا يُحمَّل بكسل Meta / Facebook، ولا Google Analytics، ولا PostHog، ولا أدوات الدردشة، ولا متتبِّعات النماذج، ولا أي نصوصٍ برمجيةٍ خاصةٍ بالتعرّف على البصمة الرقمية من أطرافٍ ثالثة.</>,
                <>وفي حال إضافة <Brand /> لأي تحليلاتٍ غير ضرورية مستقبلًا، فسيُحدَّث هذا القسم وستُدرَج آليةٌ مناسبةٌ للإشعار والموافقة قبل تفعيل تلك التقنية.</>,
            ],
        },
    },
    {
        id: "children",
        en: {
            heading: "19. Children and under-age access",
            body: [
                <>Registration is restricted to persons aged 18 and above. <Brand /> does not knowingly provide member accounts to persons under 18. On verified notice that a person under 18 has registered, the account is restricted or removed and the personal data is deleted, subject to any retention required by law, an ongoing safety matter, a dispute, or a judicial requirement.</>,
            ],
        },
        ar: {
            heading: "١٩. الأطفال ومَن هم دون السن",
            body: [
                <>يقتصر التسجيل على الأشخاص البالغين ١٨ عامًا فأكثر. ولا تُتيح <Brand /> عن علمٍ إنشاء حسابات أعضاء لمن هم دون سنّ الثامنة عشرة. وعند التحقّق من تسجيل شخصٍ دون هذه السن، يُقيَّد الحساب أو يُزال وتُحذَف البيانات الشخصية، مع مراعاة أي احتفاظٍ يستلزمه النظام، أو ملفُّ سلامةٍ قائم، أو نزاع، أو متطلَّبٌ قضائي.</>,
            ],
        },
    },
    {
        id: "responsibilities",
        en: {
            heading: "20. Member responsibilities",
            body: [
                <>Members must not upload another person&apos;s personal or sensitive data without proper authority. Members must confirm that they hold the rights to any photos, videos or GIFs they upload. Members must avoid publishing government identifiers, payment-card data, health or biometric data, or precise real-time location in public posts.</>,
            ],
        },
        ar: {
            heading: "٢٠. مسؤوليات العضو",
            body: [
                <>لا يجوز للعضو رفع بيانات شخصيةٍ أو حسّاسةٍ لشخصٍ آخر دون صلاحيةٍ مناسبة. ويجب أن يؤكّد امتلاكه لحقوق ما يرفعه من صورٍ أو مقاطع فيديو أو ملفات GIF. كما ينبغي تجنّب نشر هويات حكومية، أو بيانات بطاقات دفع، أو بياناتٍ صحّيةٍ أو حيوية، أو موقعٍ لحظيٍّ دقيق في المنشورات العامة.</>,
            ],
        },
    },
    {
        id: "changes",
        en: {
            heading: "21. Changes to this policy",
            body: [
                <>Material changes to this policy will be displayed prominently in the app and on the website, and where the law requires, fresh consent will be sought. Each version carries an effective date, and previous versions are kept internally as part of compliance records.</>,
            ],
        },
        ar: {
            heading: "٢١. تعديلات هذه السياسة",
            body: [
                <>سيُعلَن عن أي تعديلاتٍ جوهريةٍ على هذه السياسة بشكلٍ ظاهر داخل التطبيق وعلى الموقع، وستُطلَب موافقةٌ جديدة حيثما يشترط النظام ذلك. ويحمل كل إصدارٍ تاريخ سريان، وتُحفَظ الإصدارات السابقة داخليًّا ضمن سجلّات الامتثال.</>,
            ],
        },
    },
    {
        id: "language",
        en: {
            heading: "22. Language",
            body: [
                <>The Arabic and English versions of this policy are intended to match. For Saudi regulatory interpretation, the Arabic text governs to the extent legally permitted.</>,
            ],
        },
        ar: {
            heading: "٢٢. اللغة",
            body: [
                <>وُضِعت نسختا هذه السياسة العربية والإنجليزية لتتطابقا في المعنى. ولأغراض التفسير النظامي داخل المملكة، تسود النسخة العربية بالقدر الذي يُجيزه النظام.</>,
            ],
        },
    },
];

const COPY_EN = {
    overline: "Legal",
    title: <>Privacy Policy</>,
    lead: <>How <Brand /> collects, uses and protects personal data across crewznation.com, the CrewZNatioN mobile application, and all associated services.</>,
    effective: `Effective ${EFFECTIVE_EN}.`,
    controllerLine: <>Controller: {CONTROLLER_EN}. Operating from Riyadh, Kingdom of Saudi Arabia.</>,
    switchLabel: "العربية",
    metaTitle: "Privacy Policy — CrewZNatioN",
    metaDesc:
        "CrewZNatioN Privacy Policy — how personal data is handled across the website, mobile app and community services under the Saudi PDPL.",
    tocHeading: "On this page",
    backHome: "Back to CrewZNatioN",
    canonical: "https://www.crewznation.com/privacy",
};

const COPY_AR = {
    overline: "قانوني",
    title: <>سياسة الخصوصية</>,
    lead: <>كيف تقوم <Brand /> بجمع البيانات الشخصية واستخدامها وحمايتها عبر موقع crewznation.com، وتطبيق CrewZNatioN للأجهزة الجوّالة، وسائر الخدمات المرتبطة.</>,
    effective: `تاريخ السريان ${EFFECTIVE_AR}.`,
    controllerLine: <>الجهة المتحكِّمة بالبيانات: {CONTROLLER_AR}. تعمل من مدينة الرياض، المملكة العربية السعودية.</>,
    switchLabel: "English",
    metaTitle: "سياسة الخصوصية — CrewZNatioN",
    metaDesc:
        "سياسة الخصوصية الخاصة بـ CrewZNatioN — كيفية التعامل مع البيانات الشخصية عبر الموقع وتطبيق الجوّال وخدمات المجتمع وفق نظام حماية البيانات الشخصية السعودي.",
    tocHeading: "محتويات الصفحة",
    backHome: "العودة إلى CrewZNatioN",
    canonical: "https://www.crewznation.com/ar/privacy",
};

export default function Privacy({ locale = "en" }) {
    const isAr = locale === "ar";
    const Nav = isAr ? NavbarAr : Navbar;
    const Foot = isAr ? FooterAr : Footer;
    const t = isAr ? COPY_AR : COPY_EN;
    const other = isAr ? "/privacy" : "/ar/privacy";

    // Accessible title + meta description + canonical URL per
    // language. Restores previous values on unmount so navigating
    // away doesn't leave stale head tags.
    useEffect(() => {
        const prevTitle = document.title;
        document.title = t.metaTitle;
        let m = document.querySelector('meta[name="description"]');
        const prevDesc = m ? m.getAttribute("content") : null;
        if (!m) {
            m = document.createElement("meta");
            m.setAttribute("name", "description");
            document.head.appendChild(m);
        }
        m.setAttribute("content", t.metaDesc);

        let c = document.querySelector('link[rel="canonical"]');
        const prevCanonical = c ? c.getAttribute("href") : null;
        if (!c) {
            c = document.createElement("link");
            c.setAttribute("rel", "canonical");
            document.head.appendChild(c);
        }
        c.setAttribute("href", t.canonical);

        return () => {
            document.title = prevTitle;
            if (m && prevDesc !== null) m.setAttribute("content", prevDesc);
            if (c) {
                if (prevCanonical !== null) c.setAttribute("href", prevCanonical);
                else c.parentNode?.removeChild(c);
            }
        };
    }, [t.metaTitle, t.metaDesc, t.canonical]);

    return (
        <div
            className={`min-h-screen bg-[var(--crewz-bg)] text-[var(--crewz-text)] ${isAr ? "font-arabic" : "font-body"}`}
            dir={isAr ? "rtl" : "ltr"}
            data-testid={isAr ? "privacy-page-ar" : "privacy-page"}
        >
            <Nav />

            {/* HEADER */}
            <section className="px-6 md:px-12 lg:px-20 pt-24 pb-10 max-w-4xl mx-auto">
                <div className={`flex items-center gap-3 mb-5 ${isAr ? "justify-end" : ""}`}>
                    <span className="gold-rule" />
                    <p className={`overline ${isAr ? "font-arabic !text-[22.04px] md:!text-[23.8px]" : ""}`}>
                        {t.overline}
                    </p>
                </div>
                <h1
                    className={`font-display ${isAr ? "" : "text-5xl md:text-6xl leading-[0.98]"} ${isAr ? "text-4xl md:text-5xl !leading-[1.25]" : ""} text-[var(--crewz-text)] max-w-3xl`}
                    data-testid="privacy-title"
                >
                    {t.title}
                </h1>
                <p
                    className={`${isAr ? "font-arabic text-[17px] md:text-[19px] leading-[1.95]" : "font-body text-lg leading-relaxed font-light"} text-[var(--crewz-text)]/85 mt-8 max-w-2xl`}
                    data-testid="privacy-lead"
                >
                    {t.lead}
                </p>
                <p
                    className={`${isAr ? "font-arabic text-[17px] md:text-[19px] leading-[1.95]" : "font-body text-sm leading-relaxed"} text-[var(--crewz-text)]/60 mt-4 max-w-2xl`}
                    data-testid="privacy-effective"
                >
                    {t.effective}
                </p>
                <p
                    className={`${isAr ? "font-arabic text-[17px] md:text-[19px] leading-[1.95]" : "font-body text-sm leading-relaxed"} text-[var(--crewz-text)]/60 mt-1 max-w-2xl`}
                    data-testid="privacy-controller"
                >
                    {t.controllerLine}
                </p>

                {/* Language switch */}
                <div className={`mt-8 ${isAr ? "text-right" : "text-left"}`}>
                    <Link
                        to={other}
                        dir={isAr ? "ltr" : "rtl"}
                        className="inline-flex items-center gap-2 border border-[var(--crewz-gold)] text-[var(--crewz-gold)] hover:bg-[var(--crewz-gold)] hover:text-[var(--crewz-bg)] px-5 py-2 text-[12px] uppercase tracking-[0.22em] font-bold transition-colors duration-300"
                        data-testid="privacy-lang-switch"
                        aria-label={isAr ? "Switch to English" : "التبديل إلى العربية"}
                    >
                        {t.switchLabel}
                    </Link>
                </div>
            </section>

            {/* BODY — 22 sections */}
            <section
                className="px-6 md:px-12 lg:px-20 pb-16 max-w-4xl mx-auto"
                data-testid="privacy-body"
            >
                <div className="space-y-12">
                    {SECTIONS.map((s) => {
                        const c = isAr ? s.ar : s.en;
                        return (
                            <section key={s.id} id={s.id} data-testid={`privacy-section-${s.id}`}>
                                <h2
                                    className={`${isAr ? "font-arabic text-2xl md:text-3xl leading-[1.4]" : "font-display text-2xl md:text-3xl leading-[1.15]"} text-[var(--crewz-text)] mb-5 font-bold`}
                                >
                                    {c.heading}
                                </h2>
                                {c.body?.map((p, i) => (
                                    <p
                                        key={i}
                                        className={`${isAr ? "font-arabic text-[17px] md:text-[19px] leading-[1.95]" : "font-body text-base md:text-lg leading-relaxed font-light"} text-[var(--crewz-text)]/85 mb-4 break-words`}
                                    >
                                        {p}
                                    </p>
                                ))}
                                {c.list && (
                                    <ul
                                        className={`${isAr ? "font-arabic text-[17px] md:text-[19px] leading-[1.95] pr-5 space-y-2 [list-style:disc]" : "font-body text-base md:text-lg leading-relaxed font-light pl-5 space-y-2 list-disc"} text-[var(--crewz-text)]/85 mt-2 mb-4 marker:text-[var(--crewz-gold)] break-words`}
                                    >
                                        {c.list.map((li, i) => (
                                            <li key={i}>{li}</li>
                                        ))}
                                    </ul>
                                )}
                                {c.footer?.map((p, i) => (
                                    <p
                                        key={`f${i}`}
                                        className={`${isAr ? "font-arabic text-[17px] md:text-[19px] leading-[1.95]" : "font-body text-base md:text-lg leading-relaxed font-light"} text-[var(--crewz-text)]/85 mt-2 break-words`}
                                    >
                                        {p}
                                    </p>
                                ))}
                            </section>
                        );
                    })}
                </div>

                {/* Back home */}
                <div className={`mt-16 ${isAr ? "text-right" : "text-left"}`}>
                    <Link
                        to={isAr ? "/ar" : "/"}
                        className="text-[var(--crewz-gold)] hover:opacity-80 transition-opacity underline-offset-4 hover:underline text-base"
                        data-testid="privacy-back-home"
                    >
                        ← {t.backHome}
                    </Link>
                </div>
            </section>

            <Foot />
        </div>
    );
}
